import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { registerPdfFonts } from 'src/components/pdf-fonts';
import { translateKey } from 'src/locales/translate';

registerPdfFonts();

export interface TreatmentPdfItem {
  name: string;
  treatmentDate: string;
  profileName?: string;
  priceNetto?: number;
  vatRate?: number;
  priceBrutto?: number;
}

interface TreatmentsSummaryPDFProps {
  treatments: TreatmentPdfItem[];
  showPatientColumn: boolean;
  organizationName?: string;
}

const styles = StyleSheet.create({
  page: {
    padding: 50,
    fontSize: 10,
    fontFamily: 'Open Sans',
  },
  header: {
    marginBottom: 20,
    borderBottom: '1 solid #000',
    paddingBottom: 10,
  },
  organizationName: {
    fontSize: 14,
    fontFamily: 'Open Sans',
    fontWeight: 700,
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 16,
    fontFamily: 'Open Sans',
    fontWeight: 700,
    textAlign: 'center',
    marginBottom: 5,
  },
  generatedInfo: {
    fontSize: 8,
    color: '#666',
    textAlign: 'center',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#EEEEEE',
    borderBottom: '1 solid #000',
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '0.5 solid #CCC',
    paddingVertical: 5,
    paddingHorizontal: 4,
  },
  tableRowAlt: {
    flexDirection: 'row',
    borderBottom: '0.5 solid #CCC',
    paddingVertical: 5,
    paddingHorizontal: 4,
    backgroundColor: '#F9F9F9',
  },
  headerCell: {
    fontSize: 8,
    fontFamily: 'Open Sans',
    fontWeight: 700,
  },
  cell: {
    fontSize: 8,
    fontFamily: 'Open Sans',
  },
  cellRight: {
    fontSize: 8,
    fontFamily: 'Open Sans',
    textAlign: 'right',
  },
  summarySection: {
    marginTop: 15,
    borderTop: '1.5 solid #000',
    paddingTop: 10,
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingVertical: 3,
  },
  summaryLabel: {
    fontSize: 9,
    fontFamily: 'Open Sans',
    fontWeight: 700,
    width: 120,
    textAlign: 'right',
    marginRight: 10,
  },
  summaryValue: {
    fontSize: 9,
    fontFamily: 'Open Sans',
    fontWeight: 700,
    width: 80,
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 50,
    left: 50,
    right: 50,
    borderTop: '1 solid #000',
    paddingTop: 10,
    fontSize: 8,
  },
});

const COL_LP = { width: '5%' };
const COL_NAME = { width: '25%' };
const COL_PATIENT = { width: '15%' };
const COL_DATE = { width: '15%' };
const COL_NETTO = { width: '13%' };
const COL_VAT = { width: '10%' };
const COL_BRUTTO = { width: '13%' };

const COL_NAME_WIDE = { width: '30%' };
const COL_DATE_WIDE = { width: '20%' };
const COL_NETTO_WIDE = { width: '15%' };
const COL_VAT_WIDE = { width: '12%' };
const COL_BRUTTO_WIDE = { width: '15%' };

const formatPrice = (value?: number): string =>
  value != null ? `${value.toFixed(2)} ${translateKey("hardcoded.currencyPln")}` : '-';

const formatVat = (value?: number): string =>
  value != null ? `${value}%` : '-';

export const TreatmentsSummaryPDF = ({
  treatments,
  showPatientColumn,
  organizationName,
}: TreatmentsSummaryPDFProps) => {
  const totalNetto = treatments.reduce((sum, t) => sum + (t.priceNetto ?? 0), 0);
  const totalBrutto = treatments.reduce((sum, t) => sum + (t.priceBrutto ?? 0), 0);
  const totalVat = totalBrutto - totalNetto;

  const colName = showPatientColumn ? COL_NAME : COL_NAME_WIDE;
  const colDate = showPatientColumn ? COL_DATE : COL_DATE_WIDE;
  const colNetto = showPatientColumn ? COL_NETTO : COL_NETTO_WIDE;
  const colVat = showPatientColumn ? COL_VAT : COL_VAT_WIDE;
  const colBrutto = showPatientColumn ? COL_BRUTTO : COL_BRUTTO_WIDE;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {organizationName && (
          <View style={styles.header}>
            <Text style={styles.organizationName}>{organizationName}</Text>
          </View>
        )}

        <View style={styles.titleSection}>
          <Text style={styles.title}>{translateKey("hardcoded.podsumowanie_zabiegow")}</Text>
          <Text style={styles.generatedInfo}>
            Wygenerowano w systemie Vetsi Clinic w dniu{' '}
            {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: pl })}
          </Text>
        </View>

        <View style={styles.tableHeader}>
          <Text style={[styles.headerCell, COL_LP]}>Lp.</Text>
          <Text style={[styles.headerCell, colName]}>{translateKey("visit.visitCourseMenuTreatment")}</Text>
          {showPatientColumn && (
            <Text style={[styles.headerCell, COL_PATIENT]}>Pacjent</Text>
          )}
          <Text style={[styles.headerCell, colDate]}>{translateKey("visit.tableDate")}</Text>
          <Text style={[styles.headerCell, colNetto, { textAlign: 'right' }]}>
            Netto
          </Text>
          <Text style={[styles.headerCell, colVat, { textAlign: 'right' }]}>
            VAT
          </Text>
          <Text style={[styles.headerCell, colBrutto, { textAlign: 'right' }]}>
            Brutto
          </Text>
        </View>

        {treatments.map((t, idx) => (
          <View
            key={`${t.name}-${t.treatmentDate}-${idx}`}
            style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
          >
            <Text style={[styles.cell, COL_LP]}>{idx + 1}</Text>
            <Text style={[styles.cell, colName]}>{t.name}</Text>
            {showPatientColumn && (
              <Text style={[styles.cell, COL_PATIENT]}>
                {t.profileName ?? '-'}
              </Text>
            )}
            <Text style={[styles.cell, colDate]}>
              {t.treatmentDate
                ? format(new Date(t.treatmentDate), 'dd/MM/yyyy HH:mm', {
                    locale: pl,
                  })
                : '-'}
            </Text>
            <Text style={[styles.cellRight, colNetto]}>
              {formatPrice(t.priceNetto)}
            </Text>
            <Text style={[styles.cellRight, colVat]}>
              {formatVat(t.vatRate)}
            </Text>
            <Text style={[styles.cellRight, colBrutto]}>
              {formatPrice(t.priceBrutto)}
            </Text>
          </View>
        ))}

        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Suma netto:</Text>
            <Text style={styles.summaryValue}>{formatPrice(totalNetto)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Suma VAT:</Text>
            <Text style={styles.summaryValue}>{formatPrice(totalVat)}</Text>
          </View>
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Suma brutto:</Text>
            <Text style={styles.summaryValue}>{formatPrice(totalBrutto)}</Text>
          </View>
          <View style={[styles.summaryRow, { marginTop: 5 }]}>
            <Text style={styles.summaryLabel}>{translateKey("hardcoded.liczba_zabiegow")}</Text>
            <Text style={styles.summaryValue}>{treatments.length}</Text>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>
            Dokument wygenerowany automatycznie w systemie Vetsi Clinic,{' '}
            {format(new Date(), 'dd.MM.yyyy HH:mm', { locale: pl })}
          </Text>
        </View>
      </Page>
    </Document>
  );
};
